import { animate, inView } from 'motion';
import { prefersReducedMotion } from './index';

const initializedCounters = new WeakSet<HTMLElement>();

export const initStatsCounters = (): void => {
  if (prefersReducedMotion()) return;

  document
    .querySelectorAll<HTMLElement>('.stats-row .small-content-card__heading.h2')
    .forEach(heading => {
      if (initializedCounters.has(heading) || heading.dataset.statsCounter) return;

      const language = heading.closest<HTMLElement>('[lang]')?.lang;
      if (language && !/^en(?:-|$)/i.test(language)) return;

      const finalText = heading.textContent?.trim() || '';
      // Preserve unsupported/localized formats as authored instead of guessing their value.
      const parts = finalText.match(/^([^\d]*)(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?([^\d]*)$/);
      if (!parts) return;

      const [, prefix, integer, fraction = '', suffix] = parts;
      const target = Number(integer.replaceAll(',', '') + fraction);
      const precision = fraction ? fraction.length - 1 : 0;
      if (target <= 0 || target > Number.MAX_SAFE_INTEGER || precision > 20) return;

      const formatter = new Intl.NumberFormat('en-US', {
        useGrouping: integer.includes(','),
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
      });
      const scale = 10 ** precision;
      const format = (value: number) =>
        `${prefix}${formatter.format(Math.floor(value * scale) / scale)}${suffix}`;

      initializedCounters.add(heading);
      const card = heading.closest<HTMLElement>('.small-content-card') || heading;
      // The module retains its reveal; a second reveal on the card obscures the count.
      card.classList.remove('fade-in-up');

      const finalValue = document.createElement('span');
      finalValue.className = 'stats-counter__final';
      finalValue.textContent = finalText;
      finalValue.setAttribute('aria-hidden', 'true');

      const visualValue = document.createElement('span');
      visualValue.className = 'stats-counter__value';
      visualValue.textContent = format(0);
      visualValue.setAttribute('aria-hidden', 'true');

      // Keep the heading's accessible name stable throughout the decorative count.
      if (!heading.hasAttribute('aria-label') && !heading.hasAttribute('aria-labelledby')) {
        heading.setAttribute('aria-label', finalText);
      }
      heading.classList.add('stats-counter');
      heading.replaceChildren(finalValue, visualValue);
      heading.dataset.statsCounter = 'ready';

      const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      let animation: ReturnType<typeof animate> | undefined;
      let stopObserving = () => {};
      let finished = false;

      const finish = () => {
        if (finished) return;
        finished = true;
        animation?.stop();
        visualValue.textContent = finalText;
        heading.dataset.statsCounter = 'complete';
        stopObserving();
        motionPreference.removeEventListener('change', onMotionChange);
      };
      const onMotionChange = () => {
        if (motionPreference.matches) finish();
      };
      motionPreference.addEventListener('change', onMotionChange);

      stopObserving = inView(
        card,
        () => {
          if (finished) return;
          heading.dataset.statsCounter = 'running';
          animation = animate(0, target, {
            delay: 0.35,
            duration: 1.6,
            ease: 'easeOut',
            onUpdate: value => {
              if (!heading.isConnected) {
                finish();
                return;
              }
              const text = format(value);
              if (visualValue.textContent !== text) visualValue.textContent = text;
            },
            onComplete: finish
          });
          // Complete when leaving the viewport; subsequent visits never restart the count.
          return finish;
        },
        { amount: 0.25, margin: '0px 0px -10% 0px' }
      );
    });
};
